/**
 * DriverShipmentsPage — 3-tab page mirroring Flutter `driver_shipments_page.dart`:
 *   - Available: pending shipments the driver can claim
 *   - Active:    shipments the driver is currently moving (picked_up / in_transit)
 *   - Claimed:   shipments the driver claimed but not yet picked up
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader, Search, Package } from 'lucide-react';
import {
  useDriverShipments,
  usePendingShipments,
  useClaimShipment,
} from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { ShipmentCard } from '@/components/shipments/ShipmentCard';
import { Button } from '@/components/common/Button';
import type { Shipment } from '@/types/shipment';

type Tab = 'available' | 'active' | 'claimed';

export default function DriverShipmentsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('available');
  const [query, setQuery] = useState('');
  const pendingQ = usePendingShipments();
  const myQ = useDriverShipments();
  const claimMut = useClaimShipment();

  const { available, active, claimed } = useMemo(() => {
    const pending = pendingQ.data || [];
    const mine = myQ.data || [];
    return {
      available: pending,
      active: mine.filter((s) => ['picked_up', 'in_transit', 'out_for_delivery', 'at_center'].includes(s.status)),
      claimed: mine.filter((s) => ['accepted', 'awaiting_approval', 'awaiting_sender_approval'].includes(s.status)),
    };
  }, [pendingQ.data, myQ.data]);

  const list = tab === 'available' ? available : tab === 'active' ? active : claimed;
  const isLoading = tab === 'available' ? pendingQ.isLoading : myQ.isLoading;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      s.tracking_number?.toLowerCase().includes(q) ||
      s.receiver_name?.toLowerCase().includes(q) ||
      s.receiver_city?.toLowerCase().includes(q) ||
      s.pickup_city?.toLowerCase().includes(q),
    );
  }, [list, query]);

  async function handleClaim(s: Shipment) {
    if (claimMut.isPending) return;
    try {
      await claimMut.mutateAsync({ id: s.id });
    } catch (e: any) {
      alert(e?.message || 'فشل المطالبة');
    }
  }

  return (
    <AppLayout>
      <h1 className="text-xl font-bold text-text-primary mb-3">
        {t('driver_shipments', 'شحنات السائق')}
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border mb-4">
        {([
          { key: 'available', label: t('tab_available', 'متاحة'), count: available.length },
          { key: 'active',    label: t('tab_active',    'نشطة'),  count: active.length },
          { key: 'claimed',   label: t('tab_claimed',   'مطلوبة'), count: claimed.length },
        ] as const).map((tabItem) => (
          <button
            key={tabItem.key}
            onClick={() => setTab(tabItem.key)}
            className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm ${
              tab === tabItem.key ? 'border-primary text-primary' : 'border-transparent text-text-secondary'
            }`}
          >
            {tabItem.label}
            {tabItem.count > 0 && (
              <span className="ms-1.5 inline-block bg-primary/10 text-primary text-xs rounded-full px-2">
                {tabItem.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <label className="flex items-center gap-2 bg-surface border border-border rounded-xl px-3 py-2 mb-4">
        <Search size={16} className="text-text-tertiary" />
        <input
          type="search"
          placeholder={t('search_shipments_driver', 'بحث برقم تتبّع، مستلِم، مدينة…')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 outline-none bg-transparent text-sm"
        />
      </label>

      {isLoading ? (
        <div className="flex justify-center py-12 text-text-tertiary">
          <Loader size={28} className="animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-10 text-center">
          <Package size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary">
            {tab === 'available'
              ? t('no_jobs_available', 'لا شحنات متاحة قريبة')
              : tab === 'active'
              ? t('no_active_jobs', 'لا شحنات نشطة')
              : t('no_claimed_jobs', 'لا شحنات مطلوبة')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <div key={s.id}>
              <ShipmentCard shipment={s} />
              {tab === 'available' && (
                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={() => handleClaim(s)}
                    loading={claimMut.isPending}
                  >
                    {t('claim', 'مطالبة')}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick lookup CTA */}
      <div className="fixed bottom-4 end-4 sm:static sm:mt-6">
        <Link
          to="/driver/lookup"
          className="inline-flex items-center gap-2 bg-secondary text-white rounded-full px-5 py-3 shadow-lg hover:bg-secondary-light"
        >
          <Search size={16} /> {t('shortcode_lookup', 'بحث برمز مختصر')}
        </Link>
      </div>
    </AppLayout>
  );
}
